import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Check, Briefcase, GraduationCap, Building2, School } from 'lucide-react';
import { ARENA } from '../lib/classTheme';

const JOBS = [
  'Accountant','Actor','Actuary','Administrator','Advertising Manager','Agricultural Engineer','Air Traffic Controller',
  'Aircraft Mechanic','Ambulance Driver','Animator','Anthropologist','App Developer','Architect','Archivist','Artist',
  'Astronomer','Athlete','Attorney','Auditor','Baker','Bank Teller','Barber','Barista','Bartender','Beautician',
  'Biologist','Blacksmith','Blogger','Bookkeeper','Brewer','Bricklayer','Broker','Builder','Bus Driver','Butcher',
  'Cabinet Maker','Cameraman','Cardiologist','Career Counselor','Carpenter','Cartographer','Cashier','Caterer','Chef',
  'Chemical Engineer','Chemist','Chiropractor','Choreographer','Civil Engineer','Cleaner','Clerk','Coach','Composer',
  'Computer Programmer','Concierge','Conductor','Construction Worker','Consultant','Content Creator','Copywriter',
  'Counselor','Courier','Craftsman','Crane Operator','Customer Service Rep','Dancer','Data Analyst','Data Scientist',
  'Dental Hygienist','Dentist','Designer','Detective','Dietitian','Diplomat','Director','Dispatcher','DJ','Doctor',
  'Driver','Drone Operator','Economist','Editor','Electrician','Electrical Engineer','Engineer','Entrepreneur',
  'Environmental Scientist','Event Planner','Farmer','Fashion Designer','Financial Advisor','Firefighter','Fisherman',
  'Fitness Trainer','Flight Attendant','Florist','Forester','Freelancer','Game Developer','Gardener','Geologist',
  'Glazier','Graphic Designer','Hairdresser','Handyman','Hardware Engineer','Historian','HR Manager','Illustrator',
  'Industrial Designer','Influencer','Insurance Agent','Interior Designer','Interpreter','Inventory Manager',
  'IT Support','Janitor','Jeweler','Journalist','Judge','Laboratory Technician','Landscaper','Lawyer','Lecturer',
  'Librarian','Lifeguard','Linguist','Locksmith','Logistics Manager','Machinist','Makeup Artist','Manager',
  'Marine Biologist','Marketing Manager','Massage Therapist','Mathematician','Mechanic','Mechanical Engineer',
  'Medical Assistant','Meteorologist','Midwife','Miner','Model','Musician','Nail Technician','Nanny','Navigator',
  'Network Engineer','Notary','Nurse','Nutritionist','Occupational Therapist','Office Manager','Operator',
  'Optician','Optometrist','Painter','Paralegal','Paramedic','Park Ranger','Pastry Chef','Pediatrician','Pharmacist',
  'Photographer','Physical Therapist','Physician','Physicist','Pilot','Plumber','Podcaster','Police Officer',
  'Political Scientist','Postal Worker','Product Manager','Professor','Project Manager','Psychiatrist','Psychologist',
  'Public Relations','Publisher','QA Engineer','Radiologist','Real Estate Agent','Receptionist','Recruiter',
  'Researcher','Restaurant Manager','Retail Assistant','Roofer','Sailor','Sales Manager','Sales Representative',
  'Scientist','Scriptwriter','Sculptor','Security Guard','SEO Specialist','Server','Social Media Manager',
  'Social Worker','Software Developer','Software Engineer','Sound Engineer','Speech Therapist','Statistician',
  'Stockbroker','Streamer','Surgeon','Surveyor','Systems Administrator','Tailor','Tattoo Artist','Tax Advisor',
  'Taxi Driver','Teacher','Technical Writer','Technician','Therapist','Tour Guide','Trader','Train Driver',
  'Translator','Travel Agent','Truck Driver','UI Designer','Urban Planner','UX Designer','Veterinarian',
  'Video Editor','Waiter','Waitress','Warehouse Worker','Web Designer','Web Developer','Welder','Writer','Zoologist',
  'Other',
];

const FIELDS = [
  'Accounting','Aerospace Engineering','Agriculture','Anthropology','Applied Mathematics','Architecture','Art History',
  'Artificial Intelligence','Astronomy','Biochemistry','Biology','Biomedical Engineering','Business Administration',
  'Chemical Engineering','Chemistry','Civil Engineering','Communications','Computer Engineering','Computer Science',
  'Criminology','Cybersecurity','Data Science','Dentistry','Design','Economics','Education','Electrical Engineering',
  'Environmental Science','Film Studies','Finance','Fine Arts','Game Design','Geography','Geology','Graphic Design',
  'History','Hospitality','Human Resources','Industrial Engineering','Information Systems','International Relations',
  'Journalism','Languages','Law','Linguistics','Literature','Logistics','Management','Marketing','Mathematics',
  'Mechanical Engineering','Media Studies','Medicine','Music','Nursing','Nutrition','Pharmacy','Philosophy',
  'Photography','Physical Education','Physics','Political Science','Psychology','Public Health','Robotics',
  'Social Work','Sociology','Software Engineering','Sports Science','Statistics','Theatre','Tourism','Veterinary',
  'Other',
];

const TYPES = [
  { key: 'student_school',     label: 'School student',   icon: School,         sub: 'Under 18' },
  { key: 'student_university', label: 'University',       icon: GraduationCap,  sub: 'Studying' },
  { key: 'working',            label: 'Working',          icon: Briefcase,      sub: 'Employed' },
  { key: 'business_owner',     label: 'Business owner',   icon: Building2,      sub: 'I run a business' },
];

export default function OccupationPicker({ value, onChange }) {
  const [search, setSearch] = useState('');
  const type = value?.occupation;

  const list = type === 'working' ? JOBS : type === 'student_university' ? FIELDS : [];
  const filtered = useMemo(() => {
    const t = search.toLowerCase().trim();
    if (!t) return list.slice(0, 40);
    return list.filter(x => x.toLowerCase().includes(t)).slice(0, 40);
  }, [search, list]);

  return (
    <div className="space-y-3">
      <div>
        <label className="label">What do you do?</label>
        <div className="grid grid-cols-2 gap-2">
          {TYPES.map(t => {
            const active = type === t.key;
            return (
              <button key={t.key} type="button"
                onClick={() => onChange({ occupation: t.key, occupation_detail: '', company_name: '', company_website: '' })}
                className="relative p-3 rounded-xl border-2 text-left transition"
                style={active
                  ? { borderColor: ARENA.gold, background: 'rgba(201,151,58,.10)' }
                  : { borderColor: 'var(--stone-200)', background: 'var(--marble)' }}>
                {active && (
                  <Check size={13} strokeWidth={3} className="absolute top-2 right-2"
                    style={{ color: ARENA.goldD }} />
                )}
                <t.icon size={19} style={{ color: active ? ARENA.goldD : 'var(--stone-400)' }} />
                <p className="font-display font-bold text-sm mt-1.5">{t.label}</p>
                <p className="text-[10px]" style={{ color: 'var(--stone-400)' }}>{t.sub}</p>
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {(type === 'working' || type === 'student_university') && (
          <motion.div key={type} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <label className="label">
              {type === 'working' ? 'Your job' : 'Field of study'}
            </label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--stone-400)' }} />
              <input className="input pl-9" autoComplete="off"
                placeholder={type === 'working' ? 'Search your job…' : 'Search your field…'}
                value={value?.occupation_detail || search}
                onChange={e => { setSearch(e.target.value); onChange({ ...value, occupation_detail: '' }); }} />
            </div>

            {!value?.occupation_detail && (
              <div className="mt-1.5 max-h-44 overflow-y-auto rounded-xl"
                style={{ background: 'var(--marble)', border: '1px solid var(--stone-200)' }}>
                {filtered.map(j => (
                  <button key={j} type="button"
                    onClick={() => { onChange({ ...value, occupation_detail: j }); setSearch(j); }}
                    className="w-full text-left px-3 py-2 text-sm font-semibold transition hover:bg-stone-100">
                    {j}
                  </button>
                ))}
                {!filtered.length && (
                  <p className="px-3 py-3 text-sm text-center" style={{ color: 'var(--stone-400)' }}>
                    No match — you can type freely
                  </p>
                )}
              </div>
            )}
            {value?.occupation_detail && (
              <div className="mt-1.5 flex items-center gap-2 px-3 py-2 rounded-xl"
                style={{ background: 'rgba(52,211,153,.12)' }}>
                <Check size={14} className="text-mint-600" strokeWidth={3} />
                <span className="text-sm font-bold">{value.occupation_detail}</span>
                <button type="button" onClick={() => { onChange({ ...value, occupation_detail: '' }); setSearch(''); }}
                  className="ml-auto text-xs font-bold" style={{ color: 'var(--stone-400)' }}>change</button>
              </div>
            )}
          </motion.div>
        )}

        {type === 'business_owner' && (
          <motion.div key="biz" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden space-y-3">
            <div>
              <label className="label">Company name</label>
              <input className="input" value={value?.company_name || ''}
                onChange={e => onChange({ ...value, company_name: e.target.value })}
                placeholder="Acme Ltd." />
            </div>
            <div>
              <label className="label">Website (optional)</label>
              <input className="input" type="url" value={value?.company_website || ''}
                onChange={e => onChange({ ...value, company_website: e.target.value })}
                placeholder="https://example.com" />
            </div>
            <div className="p-3 rounded-xl" style={{ background: 'rgba(201,151,58,.10)' }}>
              <p className="text-[11px] font-bold" style={{ color: ARENA.goldD }}>
                💡 Want to reward your customers? You can open a merchant profile later from settings.
              </p>
            </div>
          </motion.div>
        )}

        {type === 'student_school' && (
          <motion.div key="school" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <label className="label">Grade / year (optional)</label>
            <input className="input" value={value?.occupation_detail || ''}
              onChange={e => onChange({ ...value, occupation_detail: e.target.value })}
              placeholder="e.g. 9th grade" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}